import { isDef, isDefAndNotNull } from '@utils/def';
import { first, isArray, upperFirst } from 'lodash';

import {
  MultipleAutocompleteResponse,
  OptionType,
  SingleAutocompleteResponse,
} from '../AutoComplete';

import { FormColumnsAmountTypes, FormFieldTypes, FormSizeTypes } from './enums';
import {
  FieldSchema,
  FormConfig,
  FormFieldArray,
  FormFieldCheckBox,
  FormFieldCheckBoxValueType,
  FormFieldDate,
  FormFieldDateRange,
  FormFieldDateValueType,
  FormFieldInput,
  FormFieldInputValueType,
  FormFieldNumberValueType,
  FormFieldOption,
  FormFieldSelect,
  FormFieldSelectValueType,
  FormFieldTimeValueType,
  FormFieldUnion,
  FormFieldValidationRules,
  FormFieldValue,
  FormFields,
  FormSchema,
  FormSectionTitleConfig,
  FormSectionTitleProperty,
  OneOfProperty,
  PrimitiveProperty,
  Properties,
  Property,
  Required,
  SchemaFieldArray,
  SchemaFieldBoolean,
  SchemaFieldDate,
  SchemaFieldDateRange,
  SchemaFieldNumber,
  SchemaFieldObject,
  SchemaFieldSelect,
  SchemaFieldString,
} from './types';

export const isSchemaFieldString = (
  fieldSchema: FieldSchema,
): fieldSchema is SchemaFieldString =>
  'type' in fieldSchema && fieldSchema.type === FormFieldTypes.string;
export const isSchemaFieldNumber = (
  fieldSchema: FieldSchema,
): fieldSchema is SchemaFieldNumber =>
  'type' in fieldSchema && fieldSchema.type === FormFieldTypes.number;

export const isOneOfProperty = (
  property: Property,
): property is OneOfProperty => 'oneOf' in property;
export const isConstProperty = (
  property: Property,
): property is PrimitiveProperty => 'const' in property;
export const isFormFieldHasDependantFields = (
  formField: FormFieldUnion,
): formField is FormFieldSelect =>
  'dependantFields' in formField && !!formField.dependantFields?.length;
export const isBooleanProperty = (
  property: Property,
): property is SchemaFieldBoolean =>
  'type' in property && property.type === FormFieldTypes.boolean;
export const isStringProperty = (
  property: Property,
): property is SchemaFieldString =>
  'type' in property && property.type === FormFieldTypes.string;
export const isNumberProperty = (
  property: Property,
): property is SchemaFieldNumber =>
  'type' in property && property.type === FormFieldTypes.number;
export const isArrayProperty = (
  property: Property,
): property is SchemaFieldArray =>
  'type' in property && property.type === FormFieldTypes.array;
export const isDateProperty = (
  property: Property,
): property is SchemaFieldDate =>
  'type' in property && property.type === FormFieldTypes.date;
export const isDateRangeProperty = (
  property: Property,
): property is SchemaFieldDateRange =>
  'type' in property && property.type === FormFieldTypes.dateRange;
export const isSelectProperty = (
  property: Property,
): property is SchemaFieldSelect =>
  'type' in property && property.type === FormFieldTypes.select;

export const isFormFieldSelect = (
  formField: FormFieldUnion,
): formField is FormFieldSelect =>
  'type' in formField && formField.type === FormFieldTypes.select;
export const isFormFieldCheckbox = (
  formField: FormFieldUnion,
): formField is FormFieldCheckBox =>
  'type' in formField && formField.type === FormFieldTypes.boolean;
export const isFormFieldInput = (
  formField: FormFieldUnion,
): formField is FormFieldInput =>
  'type' in formField && formField.type === FormFieldTypes.string;
export const isFormFieldNumber = (
  formField: FormFieldUnion,
): formField is FormFieldInput =>
  'type' in formField && formField.type === FormFieldTypes.number;
export const isFormFieldArray = (
  formField: FormFieldUnion,
): formField is FormFieldArray =>
  'type' in formField && formField.type === FormFieldTypes.array;
export const isFormFieldDate = (
  formField: FormFieldUnion,
): formField is FormFieldDate =>
  'type' in formField && formField.type === FormFieldTypes.date;
export const isFormFieldDateRange = (
  formField: FormFieldUnion,
): formField is FormFieldDateRange =>
  'type' in formField && formField.type === FormFieldTypes.dateRange;
export const isValueOption = (
  value: FormFieldValue | undefined,
): value is OptionType =>
  isDefAndNotNull(value) && typeof value === 'object' && 'value' in value;
const isOption = (
  val:
    | SingleAutocompleteResponse
    | SchemaFieldString['const']
    | SchemaFieldNumber['const']
    | SchemaFieldBoolean['const']
    | null
    | undefined,
): val is OptionType =>
  typeof val === 'object' &&
  val !== null &&
  'value' in (val as { value?: OptionType['value'] });

export const isFormSectionTitleProperty = (
  property: Property,
): property is FormSectionTitleProperty => {
  return property.type === FormFieldTypes.sectionTitle;
};
export const isFormSectionTitleConfig = (
  fieldConfig: FormFieldUnion,
): fieldConfig is FormSectionTitleConfig => {
  return fieldConfig.type === FormFieldTypes.sectionTitle;
};

const parseProperty = (
  propertyName: string,
  properties: Properties,
  required: Required,
  size: FormSizeTypes = FormSizeTypes.medium,
): FormFieldUnion => {
  const property = properties[propertyName];
  const isRequired = required.includes(propertyName);
  const baseProps = {
    fieldSchema: property,
    name: propertyName,
    required: isRequired,
    columnsAmount: property.columnsAmount
      ? property.columnsAmount
      : FormColumnsAmountTypes.one,
    offsetColumnsAmount: property.offsetColumnsAmount
      ? property.offsetColumnsAmount
      : undefined,
    size,
  };

  if (isFormSectionTitleProperty(property)) {
    return {
      type: FormFieldTypes.sectionTitle,
      name: property.name,
      sectionTitleType: property.sectionTitleType,
    };
  }

  if (isOneOfProperty(property)) {
    if (isSelectProperty(property)) {
      return {
        type: FormFieldTypes.select,
        ...baseProps,
        label: upperFirst(property.title || propertyName),
        showLabel: property.showLabel,
        disabled: property.disabled,
        getOptionLabel: property.getOptionLabel,
        onChangeCallback: property.onChangeCallback,
        placeholder: property.placeholder,
      };
    } else {
      return {
        type: FormFieldTypes.boolean,
        ...baseProps,
      };
    }
  }

  if (isArrayProperty(property)) {
    return {
      ...baseProps,
      label: upperFirst(property.title || propertyName),
      type: FormFieldTypes.array,
      fullRowMode: !!property.fullRowMode,
    };
  }

  const basePrimitiveProps = {
    defaultValue: isDefAndNotNull(property.default)
      ? property.default
      : property.const,
    label: upperFirst(property.title || propertyName),
    readOnly: property.readOnly,
    showLabel: property.showLabel,
  };

  if (isBooleanProperty(property)) {
    return {
      ...baseProps,
      ...basePrimitiveProps,
      disabled: property.disabled,
      type: FormFieldTypes.boolean,
      styleType: property.styleType,
      checkboxType: property.checkboxType,
    };
  }
  if (isNumberProperty(property)) {
    return {
      disabled: property.disabled,
      extraRightContent: property.extraRightContent,
      loading: property.loading,
      max: property.maximum,
      min: property.minimum,
      type: FormFieldTypes.number,
      placeholder: property.placeholder,
      prefix: property.prefix,
      postfix: property.postfix,
      step: property.step,
      ...baseProps,
      ...basePrimitiveProps,
    };
  }
  if (isStringProperty(property)) {
    return {
      ...baseProps,
      ...basePrimitiveProps,
      disabled: property.disabled,
      extraRightContent: property.extraRightContent,
      loading: property.loading,
      maxLength: property.maxLength,
      minLength: property.minLength,
      multiline: property.multiline,
      placeholder: property.placeholder,
      prefix: property.prefix,
      postfix: property.postfix,
      rows: property.rows,
      maxRows: property.maxRows,
      type: FormFieldTypes.string,
      autocomplete: property.autocomplete,
      secure: property.secure,
    };
  }
  if (isDateProperty(property)) {
    return {
      ...baseProps,
      ...basePrimitiveProps,
      disabled: property.disabled,
      format: property.format,
      maxDate: property.maxDate,
      minDate: property.minDate,
      placeholder: property.placeholder,
      type: FormFieldTypes.date,
    };
  }
  if (isDateRangeProperty(property)) {
    return {
      ...baseProps,
      ...basePrimitiveProps,
      disabled: property.disabled,
      format: property.format,
      maxDate: property.maxDate,
      minDate: property.minDate,
      placeholder: property.placeholder,
      type: FormFieldTypes.dateRange,
    };
  }
  if (isSelectProperty(property)) {
    return {
      ...baseProps,
      ...basePrimitiveProps,
      disabled: property.disabled,
      defaultValue: isDefAndNotNull(property.default)
        ? { label: property.default, value: property.default }
        : undefined,
      getOptionLabel: property.getOptionLabel,
      multiple: property.multiple,
      onChangeCallback: property.onChangeCallback,
      onInputChange: property.onInputChange,
      options: property.options,
      optionsLoading: property.optionsLoading,
      optionsWithCheckbox: property.optionsWithCheckbox,
      placeholder: property.placeholder,
      disableCloseOnSelect: property.disableCloseOnSelect,
      postfix: property.postfix,
      type: FormFieldTypes.select,
    };
  }

  return {
    type: FormFieldTypes.string,
    ...baseProps,
    ...basePrimitiveProps,
  };
};

export const schemaToFormConfig = (
  schema: FormSchema,
  skipPropNames: string[] = [],
): FormConfig => {
  const formConfig: FormConfig = [];
  const { properties, required, size } = schema as FormSchema;

  for (const propertyName in properties) {
    const propertyType = properties[propertyName].type;

    if (skipPropNames.includes(propertyName)) {
      continue;
    }

    if (isOneOfProperty(properties[propertyName])) {
      const property = properties[propertyName] as OneOfProperty;
      const formConfigItem = parseProperty(
        propertyName,
        properties,
        required,
        size,
      );
      const filteredSkipPropNames = getSkipPropNamesForOneOf(
        skipPropNames,
        propertyName,
      );
      const { selectFormConfigItem } = parseOneOf(
        propertyName,
        property,
        formConfigItem as FormFieldSelect,
        filteredSkipPropNames,
        size,
      );

      formConfig.push(selectFormConfigItem);
    } else if (propertyType === FormFieldTypes.array) {
      const formArrayConfig: FormConfig = [];
      const property = properties[propertyName] as SchemaFieldArray;
      const arrayProperties = property.properties;

      if (skipPropNames.includes(propertyName)) {
        continue;
      }
      for (const arrayPropertyName in arrayProperties) {
        const formConfigItem = parseProperty(
          arrayPropertyName,
          arrayProperties,
          required,
          size,
        );
        formArrayConfig.push({
          ...formConfigItem,
          name: arrayPropertyName,
        });
      }

      formConfig.push({
        name: propertyName,
        type: property.type,
        properties: formArrayConfig,
        fieldSchema: property,
        label: property.title,
        minRows: property.minRows || 1,
        renderActions: property.renderActions,
        columnsAmount: property.columnsAmount,
        offsetColumnsAmount: property.offsetColumnsAmount,
        defaultValue: undefined,
        value: null,
        fullRowMode: !!property.fullRowMode,
      });
    } else {
      const formConfigItem = parseProperty(
        propertyName,
        properties,
        required,
        size,
      );

      formConfig.push(formConfigItem);
    }
  }

  return formConfig;
};

const parseOneOf = (
  propertyName: string,
  property: OneOfProperty,
  selectFormConfigItem: FormFieldSelect,
  skipPropNames: string[],
  size: FormSizeTypes = FormSizeTypes.medium,
): { selectFormConfigItem: FormFieldSelect } => {
  const formConfigItem: FormFieldSelect = { ...selectFormConfigItem };
  let defaultValue = undefined;
  let label = undefined;
  let placeholder: string | undefined = undefined;
  formConfigItem.getOptionLabel = (option) => `${option.label}`;
  //formConfigItem.getOptionValue = (option) => `${option.label}`;

  formConfigItem.dependantFields = property.oneOf.map(
    (p: SchemaFieldObject) => {
      const formOptionField: FormFieldOption = {
        fieldSchema: property,
        name: formConfigItem.name,
        type: FormFieldTypes.option,
        value: undefined,
      };
      if (isStringProperty(p) && isConstProperty(p)) {
        formOptionField.value = (p as SchemaFieldString).const;
        formOptionField.label = `${(p as SchemaFieldString).const}`;
      } else {
        const dependantFields: FormConfig = [];
        const oneOfProp = p.properties[propertyName];
        placeholder = placeholder || p.description;
        formOptionField.offsetColumnsAmount = oneOfProp.offsetColumnsAmount;

        if (isConstProperty(oneOfProp)) {
          label = upperFirst(oneOfProp.title || propertyName);

          if (isBooleanProperty(oneOfProp)) {
            formOptionField.value = oneOfProp.const;
            formOptionField.label = `${oneOfProp.const}`;
          } else {
            formOptionField.value = (
              oneOfProp as SchemaFieldSelect
            ).const?.value;
            formOptionField.label = `${
              (oneOfProp as SchemaFieldSelect).const?.label
            }`;
          }

          if (isDef(oneOfProp.default)) {
            if (isBooleanProperty(oneOfProp)) {
              defaultValue = oneOfProp.default;
            } else {
              defaultValue = {
                label: `${(oneOfProp as SchemaFieldSelect).default?.label}`,
                value: (oneOfProp as SchemaFieldSelect).default?.value,
              };
            }
          }

          const dependantProperties = { ...p.properties };
          delete dependantProperties[propertyName];
          const dependantRequired = p.required || [];
          for (const dependantPropertyName in dependantProperties) {
            if (skipPropNames.includes(dependantPropertyName)) {
              continue;
            }
            if (isOneOfProperty(dependantProperties[dependantPropertyName])) {
              const dependantFormConfigItem = parseProperty(
                dependantPropertyName,
                dependantProperties,
                dependantRequired,
                size,
              );
              const filteredSkipPropNames = getSkipPropNamesForOneOf(
                skipPropNames,
                dependantPropertyName,
              );
              const { selectFormConfigItem } = parseOneOf(
                dependantPropertyName,
                dependantProperties[dependantPropertyName] as OneOfProperty,
                dependantFormConfigItem as FormFieldSelect,
                filteredSkipPropNames,
                size,
              );

              dependantFields.push(selectFormConfigItem);
            } else {
              dependantFields.push({
                ...parseProperty(
                  dependantPropertyName,
                  dependantProperties,
                  dependantRequired,
                  size,
                ),
              });
            }
          }
        }
        formOptionField.dependantFields = dependantFields;
      }
      return formOptionField;
    },
  );
  formConfigItem.label = label;
  formConfigItem.placeholder = formConfigItem.placeholder || placeholder;

  if (isDef(defaultValue)) {
    formConfigItem.defaultValue = defaultValue;
  } else {
    const firstOption = first(
      formConfigItem.dependantFields,
    ) as FormFieldOption;

    if (firstOption) {
      formConfigItem.defaultValue = {
        label: firstOption.label,
        value: firstOption.value,
      };
    }
  }
  return { selectFormConfigItem: formConfigItem };
};

export const extractValue = (
  value: FormFieldValue | FormFieldValue[] | FormFields[] | undefined,
):
  | FormFieldInputValueType
  | FormFieldCheckBoxValueType
  | FormFieldSelectValueType
  | FormFieldNumberValueType
  | FormFieldDateValueType
  | FormFieldTimeValueType
  | OptionType['value']
  | FormFields
  | FormFields[]
  | FormFieldValue[]
  | null =>
  Array.isArray(value)
    ? value
    : isValueOption(value)
      ? value.value
      : typeof value === 'string'
        ? value.trim()
        : (value as unknown as string);

const normalizeFormField = (
  formConfigItem: FormFieldUnion,
  formFields: FormFields,
) => {
  let result: FormFields = {};
  const propertyName = formConfigItem.name;
  const formField = formFields[propertyName];

  if (formConfigItem) {
    const defaultValue = normalizeDefaultValue(formConfigItem);
    const value = formField;

    if (isFormFieldHasDependantFields(formConfigItem)) {
      let value: FormFieldValue | FormFieldValue[] | FormFields[];

      if (isFormSectionTitleConfig(formConfigItem)) {
        return;
      }

      if (isDef(formField)) {
        const field = formField as FormFields;
        if (isDef(field[propertyName])) {
          value = extractValue(field[propertyName]);
        } else {
          value = extractValue(formField);
        }
      }

      const selectedOption = formConfigItem.dependantFields
        ?.filter((f) => f.type === FormFieldTypes.option)
        ?.find((f) => (f as FormFieldOption).value === value);
      const formattedValue = isFormFieldSelect(formConfigItem)
        ? {
            label: `${value}`,
            value,
          }
        : value;

      result = {
        [propertyName]: isDefAndNotNull(value) ? formattedValue : defaultValue,
        ...(selectedOption?.dependantFields || []).reduce(
          (memo, field) => ({
            ...memo,
            ...normalizeFormField(field, formFields as FormFields),
          }),
          {},
        ),
      };
    } else if (isFormFieldSelect(formConfigItem)) {
      if (formConfigItem.multiple) {
        const selectValue = value as MultipleAutocompleteResponse;
        result[propertyName] =
          isDefAndNotNull(selectValue) &&
          isArray(selectValue) &&
          selectValue.length &&
          selectValue[0].value
            ? selectValue
            : isDefAndNotNull(selectValue) && isArray(selectValue)
              ? selectValue.map((el) => ({ value: el, label: el }))
              : defaultValue;
      } else {
        const selectValue = value as SingleAutocompleteResponse;

        result[propertyName] =
          isDefAndNotNull(selectValue) &&
          isDefAndNotNull(selectValue?.value) &&
          isDef(selectValue?.label)
            ? value
            : isDefAndNotNull(selectValue)
              ? { value: selectValue, label: selectValue }
              : defaultValue;
      }
    } else if (isFormFieldArray(formConfigItem)) {
      const arrayProperties = formConfigItem.properties;
      const arrayDefaultValues: FormFields[] = [];
      const values = formField as FormFields[];

      if (isDefAndNotNull(values) && isArray(values) && values.length) {
        values.forEach((value) => {
          const rowDefaultValues: FormFields = {};
          arrayProperties.forEach((property) => {
            rowDefaultValues[property.name] = isDefAndNotNull(
              value[property.name],
            )
              ? value[property.name]
              : normalizeDefaultValue(property);
          });
          arrayDefaultValues.push(rowDefaultValues);
        });
      }

      result[propertyName] = arrayDefaultValues;
    } else {
      result[propertyName] = isDef(value) ? value : defaultValue;
    }
  }

  return result;
};

export const normalizeFields = (
  formFields: FormFields | FormFields[],
  formConfig: FormConfig,
): FormFields => {
  let result: FormFields = {};
  const values = formFields as FormFields;

  for (const formConfigItem of formConfig) {
    if (isFormSectionTitleConfig(formConfigItem)) {
      continue;
    }
    result = {
      ...result,
      ...normalizeFormField(formConfigItem, values),
    };
  }

  return result;
};

export const normalizeDefaultValue = (formConfigItem: FormFieldUnion) => {
  if (isFormSectionTitleConfig(formConfigItem)) {
    return undefined;
  }
  if (isFormFieldArray(formConfigItem)) {
    return undefined;
  }
  if (isDef(formConfigItem.defaultValue)) {
    return formConfigItem.defaultValue;
  }
  if (isFormFieldInput(formConfigItem)) {
    return '';
  }
  if (isFormFieldCheckbox(formConfigItem)) {
    return false;
  }
  if (isFormFieldDate(formConfigItem)) {
    return '';
  }
  if (isFormFieldDateRange(formConfigItem)) {
    return [null, null];
  }
  if (isFormFieldSelect(formConfigItem)) {
    if ((formConfigItem as FormFieldSelect).multiple) {
      return [];
    }
    return null;
  }

  return null;
};

export const getValidationRulesFromAField = (
  field?: FormFieldUnion,
): FormFieldValidationRules => {
  if (!field || isFormFieldArray(field) || isFormSectionTitleConfig(field)) {
    return {};
  }

  const { fieldSchema, required } = field;
  const baseRules: FormFieldValidationRules = {
    required,
  };

  if (isSchemaFieldString(fieldSchema)) {
    return {
      maxLength: fieldSchema.maxLength,
      minLength: fieldSchema.minLength,
      pattern: fieldSchema.pattern
        ? RegExp(fieldSchema.pattern, 'u')
        : undefined,
      patternErrorText: fieldSchema.patternErrorText,
      ...baseRules,
    };
  }
  if (isSchemaFieldNumber(fieldSchema)) {
    return {
      max: fieldSchema.maximum,
      min: fieldSchema.minimum,
      pattern: fieldSchema.pattern
        ? RegExp(fieldSchema.pattern, 'u')
        : RegExp(/^-?\d+(\.\d+)?$/, 'u'),
      patternErrorText: fieldSchema.patternErrorText,
      ...baseRules,
    };
  }

  return baseRules;
};

const extractPrimitive = (
  val:
    | SingleAutocompleteResponse
    | SchemaFieldString['const']
    | SchemaFieldNumber['const']
    | SchemaFieldBoolean['const']
    | null
    | undefined,
):
  | OptionType['value']
  | SchemaFieldString['const']
  | SchemaFieldNumber['const']
  | SchemaFieldBoolean['const']
  | null
  | undefined => {
  return isOption(val)
    ? val.value
    : (val as
        | SchemaFieldString['const']
        | SchemaFieldNumber['const']
        | SchemaFieldBoolean['const']
        | null
        | undefined);
};

const getSelectorConstValue = (
  prop: Property,
):
  | OptionType['value']
  | SchemaFieldString['const']
  | SchemaFieldNumber['const']
  | SchemaFieldBoolean['const']
  | undefined => {
  if (isBooleanProperty(prop)) {
    return prop.const;
  }
  if (isSelectProperty(prop)) {
    return prop.const ? prop.const.value : undefined;
  }
  if (isStringProperty(prop)) {
    return prop.const;
  }
  if (isNumberProperty(prop)) {
    return prop.const;
  }
  return undefined;
};

const getFieldSchema = (schema: FormSchema, name: string) => {
  const fieldSchema = schema.properties[name];

  if (fieldSchema) return fieldSchema;

  for (const propertyName in schema.properties) {
    const property = schema.properties[propertyName];

    if (!('oneOf' in property)) continue;

    for (const oneOfItem of property.oneOf) {
      const oneOfFieldSchema = oneOfItem.properties[name];

      if (oneOfFieldSchema) return oneOfFieldSchema;
    }
  }

  return undefined;
};

export const formatValuesFieldSelectBeforeSending = (
  values: FormFields,
  schema: FormSchema,
  setInactiveToNull: boolean = true,
) => {
  const formatedValues: FormFields = { ...values };

  //clean values for oneOf
  const pruneByOneOf = (
    currentValues: FormFields,
    properties: Properties,
  ): void => {
    for (const propName in properties) {
      const prop = properties[propName];

      if (isOneOfProperty(prop)) {
        const oneOf = prop.oneOf;
        const selectedRaw = currentValues[propName] as
          | SingleAutocompleteResponse
          | SchemaFieldString['const']
          | SchemaFieldNumber['const']
          | SchemaFieldBoolean['const']
          | null
          | undefined;
        const selected = extractPrimitive(selectedRaw);

        let activeProps: Set<string> | null = null;
        const unionProps: Set<string> = new Set();

        for (const variant of oneOf) {
          const selectorProp = variant.properties[propName];

          if (!selectorProp) continue;

          const selectorConst = getSelectorConstValue(selectorProp);
          const variantPropNames = Object.keys(variant.properties).filter(
            (n) => n !== propName,
          );
          variantPropNames.forEach((n) => unionProps.add(n));

          if (isDefAndNotNull(selected) && selectorConst === selected) {
            activeProps = new Set(variantPropNames);
          }
        }

        if (activeProps) {
          unionProps.forEach((n) => {
            if (!activeProps!.has(n)) {
              if (setInactiveToNull) {
                // set value based on schema type: boolean -> false, string -> null
                let target: boolean | null = null;
                let fieldSchema: Property | undefined = undefined;

                for (const v of oneOf) {
                  const propInVariant = v.properties[n];
                  if (propInVariant) {
                    fieldSchema = propInVariant;
                    break;
                  }
                }
                if (fieldSchema && isBooleanProperty(fieldSchema)) {
                  target = false;
                } else {
                  target = null;
                }
                currentValues[n] = target as FormFieldValue;
              } else {
                delete currentValues[n];
              }
            }
          });
        }
      }
    }
  };

  pruneByOneOf(formatedValues, schema.properties);

  Object.keys(formatedValues).forEach((name) => {
    const fieldSchema = getFieldSchema(schema, name);

    if (!fieldSchema) return;

    const currentValue = formatedValues[name];

    if (
      fieldSchema.type === FormFieldTypes.array &&
      Array.isArray(currentValue)
    ) {
      formatedValues[name] = currentValue.map((row) =>
        formatValuesFieldSelectBeforeSending(
          row as FormFields,
          {
            properties: (fieldSchema as SchemaFieldArray).properties ?? {},
            required: (fieldSchema as SchemaFieldArray).required ?? [],
          } as FormSchema,
        ),
      );

      return;
    }

    if (isSelectProperty(fieldSchema)) {
      if (fieldSchema.multiple) {
        const value = formatedValues[name] as MultipleAutocompleteResponse;

        if (Array.isArray(value)) {
          const normalized = value.map((v) => (isOption(v) ? v.value : v));
          formatedValues[name] = normalized as FormFieldValue[];
        }
      } else {
        const value = formatedValues[name] as SingleAutocompleteResponse;

        if (isOption(value) && isDefAndNotNull(value.value)) {
          formatedValues[name] = value.value as FormFieldValue;
        }
      }
    }
  });

  return formatedValues;
};

export const getGridColumnAmount = (
  columnsAmount: FormColumnsAmountTypes | undefined,
) => {
  switch (columnsAmount) {
    case FormColumnsAmountTypes.one:
      return 1;
    case FormColumnsAmountTypes.two:
      return 2;
    case FormColumnsAmountTypes.three:
      return 3;
    case FormColumnsAmountTypes.four:
      return 4;
    case FormColumnsAmountTypes.five:
      return 5;
    case FormColumnsAmountTypes.six:
      return 6;
    case FormColumnsAmountTypes.seven:
      return 7;
    case FormColumnsAmountTypes.eight:
      return 8;
    case FormColumnsAmountTypes.nine:
      return 9;
    case FormColumnsAmountTypes.ten:
      return 10;
    case FormColumnsAmountTypes.eleven:
      return 11;
    case FormColumnsAmountTypes.twelve:
      return 12;
    default:
      return 1;
  }
};

export const getSkipPropNamesForOneOf = (
  skipPropNames: string[],
  propertyName: string,
): string[] => {
  return skipPropNames
    .map((name) => name.split('.'))
    .filter((names) => names.length > 1 && names[0] === propertyName)
    .map((names) => names.slice(1).join('.'));
};
